import { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, CheckCircle, Award, TrendingDown } from "lucide-react";
import { AddSupplierInvites } from "@/components/AddSupplierInvites";
import { StartAuctionDialog } from "@/components/StartAuctionDialog";
import { ActiveVendorsPanel } from "@/components/auction/ActiveVendorsPanel";
import { AuctionCountdown } from "@/components/auction/AuctionCountdown";
import { formatInLondon, formatFullDateTime } from "@/lib/timezone";

interface RFP {
  id: string;
  title: string;
  description: string;
  category: string | null;
  budget_range: string | null;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  min_decrement: number | null;
  min_bid: number | null;
  status: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
}

interface Proposal {
  id: string;
  supplier_company_name: string;
  contact_name: string;
  contact_email: string;
  initial_bid_amount: number;
  proposal_details: string | null;
  submitted_at: string;
  selection_status: string;
}

interface Bid {
  id: string;
  proposal_id: string;
  amount: number;
  source: string;
  status: string;
  created_at: string;
  proposal: {
    supplier_company_name: string;
  };
}

const AuctionDetails = () => {
  const context = useOutletContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rfp, setRfp] = useState<RFP | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWinner, setSelectedWinner] = useState<string | null>(null);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startingAuction, setStartingAuction] = useState(false);

  if (context === "title-only") {
    return "Auction Details";
  }

  useEffect(() => {
    if (id) {
      fetchAuctionData();
      
      // Subscribe to real-time updates for bids, RFP status, and proposals
      const channel = supabase
        .channel(`rfp-${id}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "bids",
            filter: `rfp_id=eq.${id}`,
          },
          (payload) => {
            console.log("New bid received:", payload);
            fetchBids();
          }
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "bids",
            filter: `rfp_id=eq.${id}`,
          },
          (payload) => {
            console.log("Bid updated:", payload);
            // Check if a bid was confirmed (accepted by supplier)
            const newBid = payload.new as any;
            if (newBid.status === "confirmed") {
              fetchBids();
              fetchAuctionData(); // Refresh all data including RFP status
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "rfps",
            filter: `id=eq.${id}`,
          },
          (payload) => {
            console.log("RFP updated:", payload);
            const updatedRfp = payload.new as any;
            setRfp(updatedRfp);
            // If RFP is completed, show notification
            if (updatedRfp.status === "COMPLETED") {
              toast({
                title: "Auction Completed",
                description: "A supplier has accepted the offer. The auction is now complete.",
              });
              fetchProposals(); // Refresh proposals to get updated selection statuses
            }
          }
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "proposals",
            filter: `rfp_id=eq.${id}`,
          },
          (payload) => {
            console.log("Proposal updated:", payload);
            fetchProposals();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [id]);

  const fetchAuctionData = async () => {
    try {
      await Promise.all([fetchRFP(), fetchProposals(), fetchBids()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRFP = async () => {
    const { data, error } = await supabase
      .from("rfps")
      .select("*")
      .eq("id", id!)
      .single();

    if (error) throw error;
    setRfp(data);
  };

  const fetchProposals = async () => {
    const { data, error } = await supabase
      .from("proposals")
      .select("*")
      .eq("rfp_id", id!)
      .order("initial_bid_amount", { ascending: true });

    if (error) throw error;
    setProposals(data || []);
    
    // Check if any proposal has been marked as successful (winner)
    const winner = data?.find(p => p.selection_status === "successful");
    if (winner) {
      setSelectedWinner(winner.id);
    }
  };

  const fetchBids = async () => {
    const { data, error } = await supabase
      .from("bids")
      .select(`
        *,
        proposal:proposals(supplier_company_name)
      `)
      .eq("rfp_id", id!)
      .order("created_at", { ascending: false });

    if (error) throw error;
    setBids(data || []);
  };

  const startAuctionNow = async (endTime: Date) => {
    setStartingAuction(true);
    try {
      const { error } = await supabase
        .from("rfps")
        .update({ 
          status: "AUCTION_ACTIVE",
          auction_end: endTime.toISOString(),
        })
        .eq("id", id!);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Reverse auction started successfully!",
      });

      setShowStartDialog(false);
      await fetchRFP();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setStartingAuction(false);
    }
  };

  const selectWinner = async (proposalId: string) => {
    try {
      // Create/update the RFP decision record
      const { error } = await supabase
        .from("rfp_decisions")
        .upsert({
          rfp_id: id!,
          winning_proposal_id: proposalId,
          decided_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Update RFP status to COMPLETED
      await supabase
        .from("rfps")
        .update({ status: "COMPLETED" })
        .eq("id", id!);

      // Mark winning proposal as successful
      await supabase
        .from("proposals")
        .update({ selection_status: "successful" })
        .eq("id", proposalId);

      // Mark other proposals as unsuccessful
      await supabase
        .from("proposals")
        .update({ selection_status: "unsuccessful" })
        .eq("rfp_id", id!)
        .neq("id", proposalId);

      toast({
        title: "Success",
        description: "Winner selected successfully!",
      });

      setSelectedWinner(proposalId);
      fetchRFP();
      fetchProposals();
    } catch (error: any) {
      console.error("Error selecting winner:", error);
      toast({
        title: "Error",
        description: "Failed to select winner. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getLowestBid = (proposalId: string) => {
    // Only consider confirmed bids for the current bid calculation
    const confirmedBids = bids.filter(b => b.proposal_id === proposalId && b.status === 'confirmed');
    if (confirmedBids.length === 0) {
      const proposal = proposals.find(p => p.id === proposalId);
      return proposal?.initial_bid_amount || 0;
    }
    return Math.min(...confirmedBids.map(b => b.amount));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!rfp) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">RFP not found</p>
        <Button onClick={() => navigate("/auctions")} className="mt-4">
          Back to Auctions
        </Button>
      </div>
    );
  }

  const isAuctionActive = rfp.status === "AUCTION_ACTIVE";
  const isAuctionEnded = rfp.status === "AUCTION_ENDED" || rfp.status === "COMPLETED";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
      <Button variant="ghost" size="icon" onClick={() => navigate("/active")}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">{rfp.matter_name || rfp.title}</h2>
          <p className="text-muted-foreground">{rfp.description}</p>
        {(rfp.region || rfp.team) && (
          <div className="flex gap-2 mt-2">
            {rfp.region && <Badge variant="outline">{rfp.region.toUpperCase()}</Badge>}
            {rfp.team && <Badge variant="outline">{rfp.team.charAt(0).toUpperCase() + rfp.team.slice(1)}</Badge>}
          </div>
        )}
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={isAuctionActive ? "destructive" : "secondary"}>
            {rfp.status.replace(/_/g, " ")}
          </Badge>
          {rfp.status === "AWAITING_AUCTION" && (
            <Button onClick={() => setShowStartDialog(true)} variant="default">
              Start Auction Now
            </Button>
          )}
        </div>
      </div>

      {/* Metrics Grid - with fixed tile alignment */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Proposals</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold">{proposals.length}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Bids</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold">{bids.length}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Lowest Bid</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold text-green-600">
              ${proposals.length > 0 ? Math.min(...proposals.map(p => getLowestBid(p.id))).toLocaleString() : "0"}
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Auction End</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-end">
            {isAuctionActive ? (
              <AuctionCountdown 
                targetUtc={rfp.auction_end} 
                label="Ends in"
                showFullDate={true}
              />
            ) : (
              <>
                <div className="text-sm font-medium">
                  {formatInLondon(rfp.auction_end, "dd MMM yyyy")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatInLondon(rfp.auction_end, "HH:mm (zzz)")}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Vendors Panel - only show during active auction */}
      {isAuctionActive && (
        <ActiveVendorsPanel 
          rfpId={rfp.id}
          bids={bids}
          proposals={proposals}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* RFP Details Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>RFP Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rfp.matter_name && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Matter Name</p>
                <p className="text-foreground">{rfp.matter_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-foreground">{rfp.description}</p>
            </div>
            {rfp.region && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Region</p>
                <p className="text-foreground">{rfp.region.toUpperCase()}</p>
              </div>
            )}
            {rfp.team && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Team</p>
                <p className="text-foreground">{rfp.team.charAt(0).toUpperCase() + rfp.team.slice(1)}</p>
              </div>
            )}
            {rfp.category && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Category</p>
                <p className="text-foreground">{rfp.category}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-muted-foreground">Submission Deadline</p>
              <p className="text-foreground">{formatFullDateTime(rfp.submission_deadline)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Invite Additional Suppliers Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invite Additional Suppliers</CardTitle>
          </CardHeader>
          <CardContent>
            <AddSupplierInvites rfpId={rfp.id} onInvitesSent={fetchAuctionData} />
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="proposals">
        <TabsList>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
          <TabsTrigger value="bids">Live Bids</TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className="space-y-4">
          {proposals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No proposals submitted yet
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {proposals.map((proposal) => {
                const lowestBid = getLowestBid(proposal.id);
                const savings = proposal.initial_bid_amount - lowestBid;
                const isWinner = selectedWinner === proposal.id;

                return (
                  <Card 
                    key={proposal.id} 
                    className={`flex flex-col min-h-[280px] ${isWinner ? "border-green-500 border-2" : ""}`}
                  >
                    {/* Row 1: Header */}
                    <CardHeader className="pb-2 flex-shrink-0">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-base flex items-center gap-2 line-clamp-1">
                          {proposal.supplier_company_name}
                          {isWinner && <Award className="h-4 w-4 text-yellow-500 flex-shrink-0" />}
                        </CardTitle>
                      </div>
                      {/* Row 2: Contact - fixed height */}
                      <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                        {proposal.contact_name} • {proposal.contact_email}
                      </p>
                    </CardHeader>
                    
                    {/* Row 3+: Content with consistent alignment */}
                    <CardContent className="flex-1 flex flex-col">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Initial Bid</p>
                          <p className="text-sm font-semibold">
                            ${proposal.initial_bid_amount.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Current Bid</p>
                          <p className="text-sm font-semibold text-green-600">
                            ${lowestBid.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      {savings > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground">Savings</p>
                          <p className="text-sm font-semibold text-primary flex items-center">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            ${savings.toLocaleString()}
                          </p>
                        </div>
                      )}
                      
                      {proposal.proposal_details && (
                        <div className="flex-1 mb-3">
                          <p className="text-xs font-medium text-muted-foreground">Details:</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {proposal.proposal_details}
                          </p>
                        </div>
                      )}
                      
                      {/* Footer: Action - pinned to bottom */}
                      <div className="mt-auto pt-3 border-t border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {formatInLondon(proposal.submitted_at, "dd MMM HH:mm")}
                          </span>
                          {isAuctionEnded && !selectedWinner && (
                            <Button
                              onClick={() => selectWinner(proposal.id)}
                              size="sm"
                              variant="outline"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Select
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bids" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bid History</CardTitle>
              {isAuctionActive && (
                <div className="flex items-center gap-2 text-sm text-destructive animate-pulse">
                  <div className="h-2 w-2 bg-destructive rounded-full"></div>
                  Live Auction in Progress
                </div>
              )}
            </CardHeader>
            <CardContent>
              {bids.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No bids placed yet</p>
              ) : (
                <div className="space-y-2">
                  {bids.map((bid) => {
                    // Check if this bid was accepted (confirmed) and is from a requested offer
                    const proposal = proposals.find(p => p.id === bid.proposal_id);
                    const isAcceptedOffer = bid.status === 'confirmed' && bid.source === 'AUCTION_BID';
                    const isWinningBid = proposal?.selection_status === 'successful';
                    
                    return (
                      <div
                        key={bid.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isWinningBid 
                            ? 'bg-green-50 border-2 border-green-500' 
                            : 'bg-muted/30'
                        }`}
                      >
                        <div>
                          <p className={`font-medium ${isWinningBid ? 'text-green-700' : 'text-foreground'}`}>
                            {bid.proposal.supplier_company_name}
                            {isWinningBid && <Award className="h-4 w-4 text-yellow-500 inline ml-2" />}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatFullDateTime(bid.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-semibold ${isWinningBid ? 'text-green-700' : 'text-green-600'}`}>
                            ${bid.amount.toLocaleString()}
                          </p>
                          {isWinningBid ? (
                            <Badge className="text-xs bg-green-600 text-white hover:bg-green-700">
                              WINNER - ACCEPTED
                            </Badge>
                          ) : bid.status === 'requested' ? (
                            <Badge 
                              variant="secondary" 
                              className="text-xs bg-orange-100 text-orange-700"
                            >
                              REQUESTED OFFER
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              {bid.source.replace(/_/g, " ")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      <StartAuctionDialog
        open={showStartDialog}
        onOpenChange={setShowStartDialog}
        onConfirm={startAuctionNow}
        loading={startingAuction}
      />
    </div>
  );
};

export default AuctionDetails;